<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  attribute: {
    type: Object,
    required: true,
  },
  isEditingView: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update', 'delete']);

const { t } = useI18n();

const [showAttributeListDropdown, toggleAttributeListDropdown] = useToggle();

const attributeListMenuItems = computed(() => {
  return (
    props.attribute.attributeValues?.map(value => ({
      label: value,
      value,
      action: 'select',
      isSelected: value === props.attribute.value,
    })) || []
  );
});

const handleAttributeAction = async action => {
  emit('update', action.value);
  toggleAttributeListDropdown(false);
};
</script>

<template>
  <div class="flex items-center justify-start w-full min-w-0 gap-2">
    <div
      v-on-clickaway="() => toggleAttributeListDropdown(false)"
      class="relative flex items-center"
    >
      <button
        type="button"
        class="inline-flex items-center gap-1.5 h-[22px] px-2 rounded-md max-w-full text-xs font-semibold truncate transition-colors"
        :class="
          attribute.value
            ? 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
            : 'border border-dashed border-n-slate-7 text-n-slate-10 hover:border-n-brand hover:text-n-blue-text'
        "
        @click="toggleAttributeListDropdown()"
      >
        <span class="truncate">
          {{
            attribute.value ||
            t('CONTACTS_LAYOUT.SIDEBAR.ATTRIBUTES.TRIGGER.SELECT')
          }}
        </span>
      </button>
      <DropdownMenu
        v-if="showAttributeListDropdown"
        :menu-items="attributeListMenuItems"
        show-search
        class="w-48 mt-2 top-full ltr:left-0 rtl:right-0 max-h-64 overflow-y-auto"
        @action="handleAttributeAction($event)"
      />
    </div>

    <div v-if="isEditingView" class="flex items-center gap-1">
      <Button
        variant="faded"
        color="ruby"
        icon="i-lucide-trash"
        size="xs"
        class="flex-shrink-0 opacity-0 group-hover/attribute:opacity-100 hover:no-underline"
        @click="emit('delete')"
      />
    </div>
  </div>
</template>
